import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, CornerDownLeft, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useMenu } from '@/context/MenuContext';
import { getMenuIcon, toSearchableList, scoreMatch } from '@/lib/menuUtils';

/**
 * 전체 메뉴 검색 팔레트 (Ctrl+K / Cmd+K).
 * 메뉴가 40개를 넘어가면 트리 탐색보다 검색이 빠르다.
 *
 * 열릴 때만 마운트되므로(MainLayout 참조) 검색어/커서 초기화 로직이 따로 필요 없다.
 */
const MenuSearch = ({ onClose }) => {
  const navigate = useNavigate();
  const { menuTree } = useMenu();
  const [keyword, setKeyword] = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const allItems = useMemo(() => toSearchableList(menuTree), [menuTree]);

  const results = useMemo(() => {
    if (!keyword.trim()) return allItems.slice(0, 12);
    return allItems
      .map((item) => ({ item, score: scoreMatch(item, keyword) }))
      .filter((entry) => entry.score >= 0)
      .sort((a, b) => a.score - b.score || a.item.menuName.localeCompare(b.item.menuName))
      .slice(0, 20)
      .map((entry) => entry.item);
  }, [allItems, keyword]);

  // 검색어가 바뀌면 선택 위치를 처음으로 (렌더 중 상태 조정)
  const [lastKeyword, setLastKeyword] = useState(keyword);
  if (lastKeyword !== keyword) {
    setLastKeyword(keyword);
    setCursor(0);
  }

  // 마운트 직후 입력창 포커스
  useEffect(() => {
    const timer = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(timer);
  }, []);

  // 선택 항목이 화면 밖으로 나가지 않도록 스크롤
  useEffect(() => {
    listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [cursor, results]);

  const go = (item) => {
    if (!item) return;
    navigate(item.urlPath);
    onClose();
  };

  const handleKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setCursor((prev) => (results.length ? (prev + 1) % results.length : 0));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setCursor((prev) => (results.length ? (prev - 1 + results.length) % results.length : 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      go(results[cursor]);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center bg-black/55 p-4 pt-[10vh] backdrop-blur-sm"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="w-full max-w-xl overflow-hidden rounded-md bg-surface shadow-[var(--shadow-pop)] border border-app-border"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="메뉴 검색"
      >
        <div className="flex items-center gap-3 border-b border-app-border px-4">
          <Search size={18} className="shrink-0 text-app-faint" />
          <input
            ref={inputRef}
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="메뉴 검색 (예: 결재, 재고, 조직)"
            className="h-14 flex-1 bg-transparent text-base text-app-text outline-none placeholder:text-app-faint"
          />
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded p-1 text-app-faint hover:bg-surface-hover hover:text-app-text"
            aria-label="닫기"
          >
            <X size={18} />
          </button>
        </div>

        <div ref={listRef} className="max-h-[50vh] overflow-y-auto py-2">
          {results.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-app-faint">
              일치하는 메뉴가 없습니다.
            </p>
          ) : (
            results.map((item, index) => (
              <button
                key={item.menuId}
                type="button"
                data-active={index === cursor}
                onMouseEnter={() => setCursor(index)}
                onClick={() => go(item)}
                className={cn(
                  'flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors',
                  index === cursor ? 'bg-brand-soft' : 'hover:bg-surface-hover'
                )}
              >
                <span className="shrink-0 text-app-faint">{getMenuIcon(item.iconClass, 16)}</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-app-text">{item.menuName}</span>
                  <span className="block truncate text-xs text-app-muted">{item.trailText}</span>
                </span>
                {index === cursor && <CornerDownLeft size={14} className="shrink-0 text-brand" />}
              </button>
            ))
          )}
        </div>

        <div className="flex items-center gap-3 border-t border-app-border bg-surface-2 px-4 py-2 text-[11px] text-app-muted">
          <span><kbd className="rounded border border-app-border bg-surface px-1">↑</kbd> <kbd className="rounded border border-app-border bg-surface px-1">↓</kbd> 이동</span>
          <span><kbd className="rounded border border-app-border bg-surface px-1">Enter</kbd> 열기</span>
          <span><kbd className="rounded border border-app-border bg-surface px-1">Esc</kbd> 닫기</span>
        </div>
      </div>
    </div>
  );
};

export default MenuSearch;
